import { ValueOf } from "core/types";
import { getUnixTime } from "date-fns";
import { fromJS, Map } from "immutable";
import { createContext, useContext } from "react";
import { Selection, TranslationType } from "../types";
import { applyTagsLogic, getEmptySelection, isSelection } from "../utils";

export type TranslationState = {
  selection: Selection;
  translations: Map<string, TranslationType>;
};

type TranslationAction = {
  payload: TranslationType & {
    selection?: Selection;
    translations?: Map<string, TranslationType>;
  };
  type: TranslationReducerAction;
};

export const TRANSLATION_ACTIONS = {
  ADD: "add",
  DELETE: "delete",
  REFRESH: "refresh_all",
  SELECT: "select",
  UPDATE: "update",
} as const;

type TranslationReducerAction = ValueOf<typeof TRANSLATION_ACTIONS>;

export function translationsReducer(
  state: TranslationState,
  { payload, type }: TranslationAction = {} as TranslationAction,
): TranslationState {
  switch (type) {
    case TRANSLATION_ACTIONS.REFRESH:
      return payload.translations != null
        ? {
            ...state,
            translations: payload.translations,
          }
        : state;
    case TRANSLATION_ACTIONS.SELECT:
      return payload.selection != null
        ? { ...state, selection: payload.selection }
        : state;
    case TRANSLATION_ACTIONS.ADD: {
      const { id } = payload;

      if (typeof id !== "string") return state;

      return {
        selection: [id, payload],
        translations: state.translations.setIn(
          id.split("."),
          fromJS(applyTagsLogic(payload)).set(
            "created_at",
            getUnixTime(new Date()),
          ),
        ),
      };
    }
    case TRANSLATION_ACTIONS.UPDATE: {
      const { id } = payload;
      const imPath = id.split(".");

      if (typeof id !== "string" || !(state.translations.getIn(imPath) != null))
        return state;

      const computedPayload = applyTagsLogic(payload);

      return {
        selection: [id, computedPayload],
        translations: state.translations.setIn(imPath, fromJS(computedPayload)),
      };
    }
    case TRANSLATION_ACTIONS.DELETE: {
      const { id } = payload;

      return {
        selection: isSelection(state.selection, id)
          ? getEmptySelection()
          : state.selection,
        translations: state.translations.removeIn(id.split(".")),
      };
    }
    default:
      return state;
  }
}

type DispathContextType = (args: {
  payload: AnyObject;
  type: TranslationReducerAction;
}) => void;

export const DispatchContext = createContext<DispathContextType>(() => {});

export function useTranslationsDispatch(): DispathContextType {
  return useContext(DispatchContext);
}
